import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ApiService } from '../../../../../services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { AutoUnsubscribe } from '../../../../../decorators/auto-unsubscribe';
import { DocumentFileService } from '../../../../../services/document-file.service';
import { ReviewQueueItem } from '../../../../../models/review-queue-item';
import { DocumentFile, PageImages } from '../../../../../models/document-file';
import { PageTranslationsResponse, TranslationPiece } from '../../../../../models/page-translation';
import { Box } from '../models/box';
import { ReviewQueueService } from '../../../../../services/review-queue.service';
import { Table } from '../models/table';
import { AlertService } from '../../../../../services/alert.service';
import { StatusModalComponent } from '../shared/status-modal/status-modal.component';
import { NgxPopupService, NgxPopupComponent } from '../../../../shared/ngx-popups/ngx-popups/ngx-popups';
import { TABLE_ID_VALIDATION_STEP, USER_GUIDES, AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG, PHYSICAL_DOCUMENT_TYPES, DOC_TYPE_DISPLAY_NAME_LOOKUP, UNKNOWN_ERROR_TOAST_BODY, UNKNOWN_ERROR_TOAST_TITLE } from '../../../../../utils/constants';
import { SharedDataService } from '../../../../../services/shared-data.service';
import { SUPPORTED_CURRENCIES } from '../../../../../../app/utils/constants'
import { compareTablesForLabeling } from '../../../../../utils/table-ordering';
import { InstructionsStep } from '../../../../../models/instructions-step';
import { TrackingService } from '../../../../../services/tracking.service';
import { PreviousRouteService } from '../../../../../services/previous-route.service';
import { LoggingService, Logger } from '../../../../../services/logging.service';
import { UserGuideService } from '@services/user-guide.service';
import { UserGuide } from '@models/user-guide';
import { LaunchDarklyService } from '../../../../../services/launchdarkly.service';
import { StepLabelService } from '@services/step-label.service';
import { DocumentTextHighlight, MatchedTextLayer } from '../../../../shared/document-viewer/models/document-text-highlight';
import { TranslationViewStateService } from '@services/translation-view-state.service';
import { PdfSearchComponent } from '../../../../shared/document-viewer/pdf-toolbar/pdf-search/pdf-search.component';

const COMBINING_DIACRITICS_REGEX = /[̀-ͯ]/g;

export interface TableClassificationEvent {
  table: Table;
  newStatementType: string;
}

interface SearchMatch {
  ulc: { x: number, y: number };
  urc: { x: number, y: number };
  llc: { x: number, y: number };
  lrc: { x: number, y: number };
  rawText: string;
  matchedIn?: MatchedTextLayer;
  fontSize: number;
  pageSize: { width: number, height: number };
  pageNumber: number;
  occurrenceIndex?: number;
}

interface DisplayHighlight {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
  current: boolean;
  crossLayer: boolean;
  outlineOnly: boolean;
}

@Component({
    selector: 'app-review-table-identification',
    templateUrl: './review-table-identification.component.html',
    styleUrls: ['./review-table-identification.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class ReviewTableIdentificationComponent implements OnInit, OnDestroy {
  private readonly zoomIncrement = 0.2;
  private readonly minimumImgScale = 0.3;
  private resizeHandler = () => this.setBoxDrawingWidth();

  subsArr$: Subscription[] = [];
  isLoading = true;
  reviewQueueItemId = '';
  reviewQueueItem: ReviewQueueItem = null;
  documentFile: DocumentFile = null;
  tablesByPage: Array<Array<Box>> = [];

  supportedCurrencies = SUPPORTED_CURRENCIES;
  logger: Logger;
  pagesWithBoxesDrawn = [];
  showCapturedTablesOnly = false;
  pagesToShow: Array<any> = [];

  isBoxDrawingShown = true;

  useAzureEarlyClassificationUI: boolean = false;
  private activeUserGuide: UserGuide = null;
  mergeStepLabel: string = 'Data Review';
  loadedTables: Table[] = [];
  private previousBoxesByPage: Array<Array<Box>> = [];
  /** Snapshot of Azure bounding boxes at load time, keyed by Table identity (stable when coords change). */
  private originalAzureBoxByTable = new WeakMap<Table, Box>();

  // 3-pane layout properties
  activePageTrueIndex: number = 0;
  imgScale = 1;
  boxDrawingWidth: number;
  selectedSummaryTable: Table = null;
  searchQuery = '';

  viewInNativeLanguage = true;
  translationAvailable = false;
  translationLoading = false;
  translationFailed = false;
  private translationPiecesByPage = new Map<number, TranslationPiece[]>();
  private pageIsRtlByPage = new Map<number, boolean>();
  private translationSub: Subscription;
  private static readonly RTL_RANGES: Array<[number, number]> = [
    [0x0590, 0x05FF], [0x0600, 0x06FF], [0x0750, 0x077F], [0x08A0, 0x08FF], [0xFB1D, 0xFEFC],
  ];
  // Overlay font size (px, pre-scale) used when a translation piece carries no OCR-reported font size.
  private static readonly DEFAULT_TRANSLATION_FONT_SIZE_PX = 12;
  private static readonly PARAGRAPH_LINE_HEIGHT_RATIO = 1.25;
  private static readonly TRANSLATION_PIECE_BORDER_Y_PX = 2;
  private static readonly ARABIC_INDIC_DIGIT_START = 0x0660;
  private static readonly ARABIC_INDIC_DIGIT_END = 0x0669;
  private static readonly EXTENDED_ARABIC_INDIC_DIGIT_START = 0x06F0;
  private static readonly EXTENDED_ARABIC_INDIC_DIGIT_END = 0x06F9;

  // PDF search state
  searchText = '';
  searchLoading = false;
  searchError = '';
  searchPerformed = false;
  activeSearchTerm = '';
  searchHighlights: DocumentTextHighlight[] = [];
  activeHighlightIndex = -1;
  activeHighlightId: string = null;
  activeHighlightPageIndex = -1;
  private searchSub: Subscription;
  private highlightsPerPage: Map<number, DocumentTextHighlight[]> = new Map();
  private allSearchHighlights: DocumentTextHighlight[] = [];
  @ViewChild(PdfSearchComponent) pdfSearchComponent?: PdfSearchComponent;
  @ViewChildren('sidebarThumbnail') sidebarThumbnails: QueryList<ElementRef>;

  // Extended Sidebar instructions
  stepHeader = 'To digitize the statements in this document, start by drawing a box around each table with financial data you want to spread.';
  fullGif = 'Table_ID_updated_5_3_2021.gif';
  steps: InstructionsStep[] = [
    {
      title: 'Identify financials',
      body: 'Navigate pages using the preview thumbnails to find financial statement tables. Use the "Captured Tables" toggle to filter the view to only show pages with captured tables.',
      gifName: ''
    },
    {
      title: 'Draw extraction boxes',
      body: 'Click and drag to draw a box around each table.',
      gifName: ''
    },
    {
      title: 'Finish',
      body: 'When all tables are boxed, click the \'Next\' button in the upper right.',
      gifName: ''
    }
  ]
  tipsBody = 'Include the line item names, and number values for the data you want to bring in. \n\n Ignore headers, footers and other extraneous items. \n\n You can drag to resize tables after drawing them.';
  documentIsLocked: boolean = false;

  private statusModalPopup: NgxPopupComponent | null = null;

  constructor(
    private _apiService: ApiService,
    private _route: ActivatedRoute,
    private _documentFileService: DocumentFileService,
    private _reviewQueueService: ReviewQueueService,
    private _router: Router,
    private _alertService: AlertService,
    private _popupService: NgxPopupService,
    private _sharedDataService: SharedDataService,
    private _previousRouteService: PreviousRouteService,
    private _trackingService: TrackingService,
    private _loggingService: LoggingService,
    public userGuideService: UserGuideService,
    private _launchDarklyService: LaunchDarklyService,
    private _stepLabelService: StepLabelService,
    private _cdr: ChangeDetectorRef,
    private _elementRef: ElementRef,
    private _translationViewState: TranslationViewStateService,
  ) {
    this.logger = this._loggingService.rootLogger.newLogger('UserService');
  }

  ngOnInit() {
    this.activeUserGuide = this._stepLabelService.isEarlyClassificationEnabled
      ? USER_GUIDES.CLASSIFYING_TABLES
      : USER_GUIDES.IDENTIFYING_TABLES;
    this.userGuideService.add(this.activeUserGuide);
    this.mergeStepLabel = this._stepLabelService.getStepLabel('DATA_REVIEW_FORMAL');

    this._sharedDataService.hideDefaultNavigation();

    this.initFeatureFlags();
    this.setBoxDrawingWidth();
    window.addEventListener('resize', this.resizeHandler);

    this._sharedDataService.setHILExitUrl(this._previousRouteService.getPreviousUrl());
    this.subsArr$.push(this._route.parent.paramMap.subscribe((params => {
      this.reviewQueueItemId = params.get('uuid');
      if (!this._reviewQueueService.isValidUUID(this.reviewQueueItemId)) {
        this._router.navigate(['/404']);
        return;
      }
      this.loadData();
    })));
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
    if (this.activeUserGuide) {
      this.userGuideService.remove(this.activeUserGuide);
    }
    this._sharedDataService.showDefaultNavigation();
    this.searchSub?.unsubscribe();
    this.translationSub?.unsubscribe();
  }

  loadData() {
    this.retrieveReviewQueueItem();
  }

  next(): void {
    this.nextStep(true);
  }

  save(): void {
    this.nextStep(false);
  }

  /**
   * Converts a box used by the front-end interface to a box used by the backend doc parsing
   * @param box
   */
  toAPIBox(box: Box) {
    return {
      'ulc': {
        'x': box.x1,
        'y': box.y1,
      },
      'urc': {
        'x': box.x2,
        'y': box.y1,
      },
      'llc': {
        'x': box.x1,
        'y': box.y2,
      },
      'lrc': {
        'x': box.x2,
        'y': box.y2
      },
      'pageSize': {
        'width': box.pageWidth,
        'height': box.pageHeight,
      }
    };
  }

  showStatusPopup(): Promise<NgxPopupComponent> {
    try {
      this._trackingService.trackHumanInLoop({
        type: 'End',
        step: 'Table Identification',
        documentFileId: this.reviewQueueItem.fileKey.split('/')[0],
        documentCompanyId: this.reviewQueueItem.processingJobPayload.company.id,
        documentTenantId: this.reviewQueueItem.processingJobPayload.context.bankId
      });
    } catch (err) {
      this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
    }

    return this._popupService.open({
      componentType: StatusModalComponent,
      cssClass: 'modal-pipeline-processing',
      inputs: {
        reviewQueueItemId: this.reviewQueueItemId,
        fromStep: TABLE_ID_VALIDATION_STEP,
      },
    }).then((popup: NgxPopupComponent) => {
      this.statusModalPopup = popup;
      return popup;
    });
  }

  /**
   * Generates a list of all the tables to send to the back end
   */
  getSaveData() {
    return this.tablesByPage.map((tablesOnPage, pageIdx) => {
      return tablesOnPage.map(box => {
        const data: any = {
          'sourcePageNumber': pageIdx,
          'boundingBox': this.toAPIBox(box),
        };
        
        // Find matching table by coordinates - needed for origin preservation
        const matchingTable = this.loadedTables.find(t =>
          t.sourcePageNumber === pageIdx && 
          t.boundingBox && 
          this.areBoxesSameRegion(t.boundingBox, box)
        );
        
        // Always preserve origin data regardless of UI flag
        if (matchingTable?.origin?.startsWith('azure_document_intelligence')) {
          const originalBox = this.originalAzureBoxByTable.get(matchingTable);
          const isModified = originalBox ? !this.areBoxesSameRegion(originalBox, box) : false;
          data['origin'] = isModified ? 'user' : matchingTable.origin;
        } else {
          data['origin'] = matchingTable?.origin || null;
        }
        
        if (this.useAzureEarlyClassificationUI) {
          data['statement_type'] = matchingTable?.statementType || 'OTHER';
        }
        return data;
      });
    });
  }

  boxesChanged({boxes, activePageIndex}: { boxes: Array<Box>, activePageIndex: number }): void {
    this.tablesByPage[activePageIndex] = boxes;
    this.updateBoxDrawnImages();
  }

  updateBoxDrawnImages(): void {
    this.pagesWithBoxesDrawn = [];
    this.tablesByPage.forEach( (arrayOfBoxes, index) => {
      if (arrayOfBoxes.length > 0) {
        this.pagesWithBoxesDrawn.push(this.documentFile.pageImages[index]);
      }
    });

    this.setPagesToShow();

    if (this.searchPerformed && this.showCapturedTablesOnly) {
      this.applySearchHighlights(this.allSearchHighlights);
    }
  }

  isABoxDrawn(): boolean {
    let isFound = false;
    this.tablesByPage.forEach( (arrayOfBoxes, index) => {
      if (arrayOfBoxes.length > 0) {
        isFound = true;
        return;
      }
    });
    return isFound;
  }

  toggleCapturedTablesFilter(): void {
    this.showCapturedTablesOnly = !this.showCapturedTablesOnly;
    this.setPagesToShow();
    if (this.searchPerformed) {
      this.applySearchHighlights(this.allSearchHighlights);
    }
  }

  setPagesToShow(): void {
    if (!this.showCapturedTablesOnly || this.pagesWithBoxesDrawn.length === 0) {
      this.pagesToShow = this.documentFile.pageImages;
    } else {
      this.pagesToShow = this.pagesWithBoxesDrawn;
    }
  }

  async nextStep(next: boolean): Promise<void> {
    if (!this.isABoxDrawn()) {
      this._alertService.error('Please draw at least one box');
      return;
    }
    if (next) {
      await this.showStatusPopup().catch(() => {});
    } else {
      this.isLoading = true;
    }

    this.subsArr$.push(
      this._documentFileService.updateDocumentFile(this.documentFile.id, {
        currency: this.documentFile.currency
      }).pipe(
        switchMap(_ => this._apiService.send('Post', '/api/document-files/' + this.documentFile.id + '/hil-data', {
          'hil_data': this.getSaveData(),
          'next': next,
          'lock_key': this.reviewQueueItem.lockKey,
          'current_step': TABLE_ID_VALIDATION_STEP,
        })),
        finalize(() => this.isLoading = false)
      ).subscribe(data => {
        if (!next) {
          this._alertService.success('Data saved.');
        }
      }, err => {
        if (next && this.statusModalPopup) {
          this.statusModalPopup.closable = true;
          this._popupService.close(this.statusModalPopup);
          this.statusModalPopup = null;
        }
        if (err?.message && err.message !== 'undefined') {
          const title = err?.error_title ? err.error_title : UNKNOWN_ERROR_TOAST_TITLE;
          this._alertService.error(err.message, title);
        } else {
          this._alertService.error(UNKNOWN_ERROR_TOAST_BODY, UNKNOWN_ERROR_TOAST_TITLE);
        }
      })
    );
  }


  updateBoxLabels(): void {
    if (!this.useAzureEarlyClassificationUI) return;

    const sortedTables = [...this.loadedTables].sort(compareTablesForLabeling);

    const coreTypeCounts: Record<string, number> = {};
    let additionalCount = 0;

    sortedTables.forEach(table => {
      if (!table.boundingBox) return;

      const isCoreType = PHYSICAL_DOCUMENT_TYPES.includes(table.statementType);
      let label: string;

      if (isCoreType) {
        const baseLabel = DOC_TYPE_DISPLAY_NAME_LOOKUP[table.statementType] || table.statementType;
        coreTypeCounts[table.statementType] = (coreTypeCounts[table.statementType] || 0) + 1;
        label = coreTypeCounts[table.statementType] > 1
          ? `${baseLabel} ${coreTypeCounts[table.statementType]}`
          : baseLabel;
      } else {
        additionalCount++;
        label = additionalCount === 1 ? 'Additional Table' : `Additional Table ${additionalCount}`;
      }

      table.boundingBox.label = label;
    });

    this.syncBoxState();
  }

  private syncBoxState(): void {
    this.loadedTables.forEach(table => {
      if (table.boundingBox) {
        table.boundingBox.selected = table === this.selectedSummaryTable;
      }
    });
    this.tablesByPage = this.tablesByPage.map(page => page ? [...page] : page);
  }

  loadTables(): void {
    this.subsArr$.push(this._documentFileService.loadTables(this.documentFile.id, true).subscribe((tables: Array<Table>) => {
      this.loadedTables = tables;
      
      this.originalAzureBoxByTable = new WeakMap<Table, Box>();
      tables.forEach(table => {
        if (table.origin?.startsWith('azure_document_intelligence') && table.boundingBox) {
          this.originalAzureBoxByTable.set(table, table.boundingBox.copy());
        }
      });
      
      tables.forEach(table => {
        if (table.boundingBox !== null) {
          if (this.tablesByPage[table.sourcePageNumber]) {
            if (table.origin) {
              table.boundingBox.origin = table.origin;
            }
            this.tablesByPage[table.sourcePageNumber].push(table.boundingBox);
          }
        }
      });

      this.documentFile.pageImages.forEach((pi, idx) => {
        this.previousBoxesByPage[idx] = [...(this.tablesByPage[idx] || [])];
      });

      this.updateBoxLabels();
      this.updateBoxDrawnImages();
      this.setPagesToShow();
      this.isLoading = false;
      setTimeout(() => this.setBoxDrawingWidth());
      this.loadTranslations();
    }, err => {
      this._alertService.error(err);
    }));
  }

  changeCurrency(currency): void {
    this.documentFile.currency = currency;
  }

  toggleStepsToSuccess() {
    if (this._sharedDataService.shouldShowManualReviewSidebar === true) {
      this._sharedDataService.shouldShowManualReviewSidebar = false;
    } else {
      this._sharedDataService.shouldShowManualReviewSidebar = true;
    }
  }

  retrieveReviewQueueItem(): void {
    this.subsArr$.push(this._reviewQueueService.retrieveAndLock(this.reviewQueueItemId).subscribe((item: ReviewQueueItem) => {
      this.reviewQueueItem = item;
      this.documentIsLocked = this._reviewQueueService.isDocumentLocked(item);
      if (this.reviewQueueItem && this.reviewQueueItem.embeddedWorkflow) {
        this._sharedDataService.embeddedWorkflow$.next(this.reviewQueueItem.embeddedWorkflow);
      }
      this.retrieveDocumentFile();
      try {
        this._trackingService.trackHumanInLoop({
          type: 'Start',
          step: 'Table Identification',
          documentFileId: this.reviewQueueItem.fileKey.split('/')[0],
          documentCompanyId: this.reviewQueueItem.processingJobPayload.company.id,
          documentTenantId: this.reviewQueueItem.processingJobPayload.context.bankId
        });
      } catch (err) {
        this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
      }

    }, err => {
      this._alertService.error(err);
    }));
  }

  toggleBoxDrawing(): void {
    this.isBoxDrawingShown = !this.isBoxDrawingShown;
  }

  private retrieveDocumentFile(): void {
    // Set the bank ID to the one from the queue item
    this._apiService.setTenantName(this.reviewQueueItem.bankId);
    this.subsArr$.push(this._documentFileService.listDocumentFiles(null, this.reviewQueueItem.documentFileId, true).subscribe(docs => {
      this.documentFile = docs[0];
      this.documentFile.pageImages.forEach((pi, idx) => {
        this.tablesByPage[idx] = [];
        this.previousBoxesByPage[idx] = [];
      });
      this.loadTables();
    }, err => {
      alert(err);
    }));
  }

  private initFeatureFlags(): void {
    this.useAzureEarlyClassificationUI = this._launchDarklyService.flags[AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG];

    this.subsArr$.push(this._launchDarklyService.flagChange.subscribe(flags => {
      this.useAzureEarlyClassificationUI = flags[AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG];
    }));
  }

  onClassifyTable(event: TableClassificationEvent): void {
    const { table, newStatementType } = event;
    table.statementType = newStatementType;
    // Create new array reference to trigger change detection
    this.loadedTables = [...this.loadedTables];
    this.updateBoxLabels();
  }

  navigateToTablePage(table: Table): void {
    if (this.documentFile?.pageImages) {
      const pageIndex = table.sourcePageNumber;
      const page = this.documentFile.pageImages.find(pi => pi.pageIndex === pageIndex);
      if (page) {
        this.pagesToShow = this.documentFile.pageImages;
      }
    }
  }

  boxesChangedRedesigned({boxes, activePageIndex}: { boxes: Array<Box>, activePageIndex: number }): void {
    if (this.useAzureEarlyClassificationUI) {
      const previousBoxes = this.previousBoxesByPage[activePageIndex] || [];
      const addedBoxes = boxes.filter(box => !previousBoxes.includes(box));
      const removedBoxes = previousBoxes.filter(box => !boxes.includes(box));

      if (addedBoxes.length > 0) {
        const newTables = addedBoxes.map(box => {
          const newTable = new Table();
          newTable.boundingBox = box;
          newTable.sourcePageNumber = activePageIndex;
          newTable.statementType = 'OTHER';
          newTable.origin = 'user';
          return newTable;
        });
        this.loadedTables = [...this.loadedTables, ...newTables];
      }

      if (removedBoxes.length > 0) {
        const removedTables = this.loadedTables.filter(t =>
          t.sourcePageNumber === activePageIndex && t.boundingBox && removedBoxes.includes(t.boundingBox)
        );
        if (removedTables.length > 0) {
          this.loadedTables = this.loadedTables.filter(t => !removedTables.includes(t));
        }
      }

      this.previousBoxesByPage[activePageIndex] = [...boxes];
      this.updateBoxLabels();
    }
    this.tablesByPage[activePageIndex] = boxes;
    this.updateBoxDrawnImages();
  }

  // 3-pane layout methods
  zoomIn(): void {
    this.imgScale += this.zoomIncrement;
  }

  zoomOut(): void {
    if (this.imgScale > this.minimumImgScale) {
      this.imgScale -= this.zoomIncrement;
    }
  }

  mathFloor(value: number): number {
    return Math.floor(value);
  }

  goToPage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const pageNum = parseInt(input?.value, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= (this.documentFile?.pageImages?.length ?? 0)) {
      this.activePageTrueIndex = pageNum - 1; // convert to 0-based
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  goToPrevPage(): void {
    if (this.activePageTrueIndex > 0) {
      this.activePageTrueIndex--;
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  goToNextPage(): void {
    const max = (this.documentFile?.pageImages?.length ?? 1) - 1;
    if (this.activePageTrueIndex < max) {
      this.activePageTrueIndex++;
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  activatePageRedesigned(pageIndex: number): void {
    this.activePageTrueIndex = pageIndex;
    this.isBoxDrawingShown = true;
    this.syncBoxState();
  }

  private scrollSidebarToActivePage(): void {
    const thumbnailArray = this.sidebarThumbnails?.toArray();
    if (!thumbnailArray?.length) return;

    const pages = this.filteredPagesToShow;
    const targetIdx = pages.findIndex(p => p.pageIndex === this.activePageTrueIndex);
    if (targetIdx < 0 || targetIdx >= thumbnailArray.length) return;

    const targetEl = thumbnailArray[targetIdx]?.nativeElement as HTMLElement;
    if (!targetEl) return;

    const sidebarBody = targetEl.closest('.thumbnail-sidebar-body') as HTMLElement;
    if (!sidebarBody) return;

    const targetScrollTop = targetEl.offsetTop - sidebarBody.clientHeight / 2 + targetEl.clientHeight / 2;
    sidebarBody.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }

  getCurrentPageImage(): PageImages | null {
    if (!this.documentFile?.pageImages) return null;
    return this.documentFile.pageImages.find(pi => pi.pageIndex === this.activePageTrueIndex) ?? null;
  }

  onViewTableFromSummary(table: Table): void {
    // Navigate to the table's page and select it
    if (this.documentFile?.pageImages) {
      this.activePageTrueIndex = table.sourcePageNumber;
      this.selectedSummaryTable = table;
      this.isBoxDrawingShown = true;
      this.syncBoxState();
      setTimeout(() => this.scrollSidebarToActivePage());
    }
  }

  onReclassifyTable(event: { table: Table, newType: string }): void {
    // Convert to the expected format and update the table
    const classificationEvent: TableClassificationEvent = {
      table: event.table,
      newStatementType: event.newType
    };
    this.onClassifyTable(classificationEvent);
  }

  onUnlinkTable(table: Table): void {
    this.loadedTables = this.loadedTables.filter(t => t !== table);
    if (this.tablesByPage[table.sourcePageNumber]) {
      this.tablesByPage[table.sourcePageNumber] = this.tablesByPage[table.sourcePageNumber].filter(
        box => box !== table.boundingBox
      );
      this.previousBoxesByPage[table.sourcePageNumber] = [...this.tablesByPage[table.sourcePageNumber]];
    }
    if (this.selectedSummaryTable === table) {
      this.selectedSummaryTable = null;
    }
    this.updateBoxLabels();
  }

  trackByPageIndex(_: number, page: PageImages): number {
    return page.pageIndex;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
  }

  get filteredPagesToShow(): PageImages[] {
    if (!this.documentFile?.pageImages) return [];

    if (this.searchPerformed && this.searchHighlights.length > 0) {
      const matchingPageNumbers = new Set(this.searchHighlights.map(h => h.pageNumber));
      return this.documentFile.pageImages.filter(page => matchingPageNumbers.has(page.pageIndex + 1));
    }

    if (!this.searchQuery.trim()) return this.pagesToShow;
    const searchNum = parseInt(this.searchQuery, 10);
    if (!isNaN(searchNum)) {
      return this.pagesToShow.filter(page => (page.pageIndex + 1) === searchNum);
    }

    return this.pagesToShow;
  }

  // --- PDF Search methods ---

  get totalMatches(): number {
    return this.searchHighlights?.length || 0;
  }

  get currentMatchNumber(): number {
    return this.activeHighlightIndex >= 0 ? this.activeHighlightIndex + 1 : 0;
  }

  get currentMatchNote(): string {
    const active = this.searchHighlights[this.activeHighlightIndex];
    if (!active || this.isMatchVisibleInCurrentLayer(active.matchedIn)) {
      return '';
    }
    return active.matchedIn === 'translated'
      ? 'matched in translated document'
      : 'matched in original document';
  }

  private resetSearchState(keepText: boolean): void {
    if (!keepText) {
      this.searchText = '';
    }
    this.searchPerformed = false;
    this.activeSearchTerm = '';
    this.searchHighlights = [];
    this.allSearchHighlights = [];
    this.activeHighlightIndex = -1;
    this.activeHighlightId = null;
    this.activeHighlightPageIndex = -1;
    this.highlightsPerPage = new Map();
    this.searchError = '';
  }

  onSearchTextChange(value: string): void {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.searchText = value;
    this.resetSearchState(true);
    this._cdr.detectChanges();
  }

  onSearchSubmit(): void {
    if (this.searchLoading) {
      return;
    }

    const trimmed = (this.searchText || '').trim();
    if (!trimmed) {
      this.clearSearch(true);
      return;
    }

    this.resetSearchState(true);
    this.activeSearchTerm = trimmed;
    this._cdr.detectChanges();

    const documentFileId = Number(this.documentFile?.id);
    if (!documentFileId || isNaN(documentFileId)) {
      this.searchError = 'Document is not available for search.';
      return;
    }

    this.searchError = '';
    this.searchLoading = true;
    this.searchSub?.unsubscribe();
    this._cdr.detectChanges();

    this.searchSub = this._documentFileService.searchPageText(documentFileId, trimmed)
      .pipe(finalize(() => {
        this.searchLoading = false;
        this._cdr.detectChanges();
      }))
      .subscribe({
        next: (resp) => this.handleSearchResponse(resp),
        error: () => {
          this.searchError = 'Unable to search the document. Please try again.';
          this.clearSearch(true);
        }
      });
  }

  handleSearchResponse(resp: any): void {
    // Original-language matches come from the backend (precise word-level
    // coordinates). Translation matches are found here, in the English text the
    // overlay already loaded, so no backend change is needed (AUTO-3426).
    const term = this.normalizeForSearch((this.activeSearchTerm || '').trim());
    const originalMatches = this.assignBackendMatchLayer(this.extractBackendMatches(resp), term);
    const translatedMatches = this.buildTranslatedMatches(term);
    const merged = [...originalMatches, ...translatedMatches].sort(this.compareMatchesInReadingOrder);
    this.applySearchHighlights(this.buildHighlightsFromMatches(merged));
  }

  private compareMatchesInReadingOrder(a: SearchMatch, b: SearchMatch): number {
    return (a.pageNumber - b.pageNumber)
      || (a.ulc.y - b.ulc.y)
      || (a.ulc.x - b.ulc.x)
      || ((a.occurrenceIndex ?? 0) - (b.occurrenceIndex ?? 0));
  }

  private extractBackendMatches(resp: any): SearchMatch[] {
    const responseObjects = resp?.response?.objects || [];
    const matches: SearchMatch[] = [];
    responseObjects.forEach(obj => {
      if (obj?.matches?.length) {
        matches.push(...obj.matches);
      }
    });
    return matches;
  }

  private assignBackendMatchLayer(matches: SearchMatch[], normalizedTerm: string): SearchMatch[] {
    // Backend matches are original-language hits. Tag 'both' when the same block's
    // translation also contains the term, else 'original' (AUTO-3426).
    if (!normalizedTerm) {
      return matches;
    }
    const tagged = matches.map(match => {
      const piece = this.findCoveringPieceOnPage(
        match.pageNumber - 1, (match.ulc.x + match.lrc.x) / 2, (match.ulc.y + match.lrc.y) / 2
      );
      const inTranslated = !!piece && this.textContainsTerm(piece.translatedRawText, normalizedTerm);
      const matchedIn: MatchedTextLayer = inTranslated ? 'both' : 'original';
      return { match: { ...match, matchedIn }, piece };
    });
    this.assignOccurrenceIndexPerPiece(tagged);
    return tagged.map(entry => entry.match);
  }

  private assignOccurrenceIndexPerPiece(
    tagged: Array<{ match: SearchMatch, piece: TranslationPiece | null }>
  ): void {
    const matchesByPiece = new Map<TranslationPiece, SearchMatch[]>();
    for (const { match, piece } of tagged) {
      if (!piece) {
        continue;
      }
      if (!matchesByPiece.has(piece)) {
        matchesByPiece.set(piece, []);
      }
      matchesByPiece.get(piece).push(match);
    }
    matchesByPiece.forEach(pieceMatches => {
      [...pieceMatches]
        .sort(this.compareMatchesInReadingOrder)
        .forEach((match, occurrenceIndex) => match.occurrenceIndex = occurrenceIndex);
    });
  }

  private buildTranslatedMatches(normalizedTerm: string): SearchMatch[] {
    if (!normalizedTerm) {
      return [];
    }
    const matches: SearchMatch[] = [];
    this.translationPiecesByPage.forEach((pieces, pageIndex) => {
      for (const piece of pieces) {
        if (piece.isNumeric) {
          continue;
        }
        if (this.textContainsTerm(piece.rawText, normalizedTerm)) {
          continue;
        }
        const occurrences = this.findTermRanges(piece.translatedRawText, normalizedTerm).length;
        for (let i = 0; i < occurrences; i++) {
          matches.push({
            ulc: piece.ulc, urc: piece.urc, llc: piece.llc, lrc: piece.lrc,
            rawText: piece.translatedRawText,
            matchedIn: 'translated',
            fontSize: piece.fontSize,
            pageSize: piece.pageSize,
            pageNumber: pageIndex + 1,
            occurrenceIndex: i,
          });
        }
      }
    });
    return matches;
  }

  private pieceContaining(pieces: TranslationPiece[], x: number, y: number): TranslationPiece | null {
    return pieces.find(p =>
      p.ulc.x <= x && x <= p.lrc.x && p.ulc.y <= y && y <= p.lrc.y
    ) || null;
  }

  private findCoveringPieceOnPage(pageIndex: number, x: number, y: number): TranslationPiece | null {
    return this.pieceContaining(this.translationPiecesByPage.get(pageIndex) || [], x, y);
  }

  private textContainsTerm(text: string, normalizedTerm: string): boolean {
    return !!text && this.normalizeForSearch(text).indexOf(normalizedTerm) > -1;
  }

  buildHighlightsFromMatches(matches: SearchMatch[]): DocumentTextHighlight[] {
    if (!matches?.length) {
      return [];
    }
    return matches.map((match, idx) => ({
      id: `highlight-${idx}`,
      pageNumber: match.pageNumber,
      pageSize: match.pageSize,
      x1: match.ulc.x,
      y1: match.ulc.y,
      x2: match.lrc.x,
      y2: match.llc.y,
      matchedIn: match.matchedIn,
      occurrenceIndex: match.occurrenceIndex
    }));
  }

  applySearchHighlights(highlights: DocumentTextHighlight[]): void {
    this.searchPerformed = true;
    this.allSearchHighlights = highlights;
    const capturedFilteredHighlights = this.filterHighlightsByCapturedTables(highlights);

    if (!capturedFilteredHighlights.length) {
      this.searchHighlights = [];
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this.highlightsPerPage = new Map();
      if (this.showCapturedTablesOnly && this.pagesWithBoxesDrawn.length > 0) {
        const activeIsCaptured = this.pagesWithBoxesDrawn.some(p => p.pageIndex === this.activePageTrueIndex);
        if (!activeIsCaptured) {
          this.activePageTrueIndex = this.pagesWithBoxesDrawn[0].pageIndex;
          setTimeout(() => this.scrollSidebarToActivePage());
        }
      }
      this._cdr.detectChanges();
      return;
    }

    this.searchHighlights = capturedFilteredHighlights;
    this.buildHighlightPageCache();
    this.activeHighlightIndex = 0;
    this.activeHighlightId = this.searchHighlights[0].id;
    this.updateActiveHighlightPageIndex();
    this.searchError = '';
    this.scrollToActiveHighlight(true);
    this._cdr.detectChanges();
  }

  private filterHighlightsByCapturedTables(highlights: DocumentTextHighlight[]): DocumentTextHighlight[] {
    if (this.showCapturedTablesOnly && this.pagesWithBoxesDrawn.length > 0) {
      const capturedPageNumbers = new Set(this.pagesWithBoxesDrawn.map(p => p.pageIndex + 1));
      return highlights.filter(h => capturedPageNumbers.has(h.pageNumber));
    }
    return highlights;
  }

  private buildHighlightPageCache(): void {
    this.highlightsPerPage = new Map();
    for (const h of this.searchHighlights) {
      if (!this.highlightsPerPage.has(h.pageNumber)) {
        this.highlightsPerPage.set(h.pageNumber, []);
      }
      this.highlightsPerPage.get(h.pageNumber).push(h);
    }
  }

  private updateActiveHighlightPageIndex(): void {
    if (this.activeHighlightIndex >= 0 && this.activeHighlightIndex < this.searchHighlights.length) {
      this.activeHighlightPageIndex = this.searchHighlights[this.activeHighlightIndex].pageNumber - 1;
    } else {
      this.activeHighlightPageIndex = -1;
    }
  }

  getHighlightsForCurrentPage(): DocumentTextHighlight[] {
    return this.highlightsPerPage.get(this.activePageTrueIndex + 1) || [];
  }

  getDisplayHighlightsForCurrentPage(): DisplayHighlight[] {
    const highlights = this.getHighlightsForCurrentPage();
    if (!highlights.length) return [];

    const renderedWidth = this.boxDrawingWidth * this.imgScale;
    const overlayShown = this.shouldShowTranslationOverlay();
    const boxesByRegion = new Map<string, DisplayHighlight>();

    for (const h of highlights) {
      const crossLayer = !this.isMatchVisibleInCurrentLayer(h.matchedIn);
      const current = h.id === this.activeHighlightId;
      if (!crossLayer && overlayShown && !current) {
        continue;
      }
      const pageWidth = h.pageSize?.width || 1;
      const scale = renderedWidth / pageWidth;
      const block = (overlayShown || crossLayer) ? this.coveringPieceRect(h) : null;
      const rect = block || { x1: h.x1, y1: h.y1, x2: h.x2, y2: h.y2 };
      const regionKey = `${rect.x1}:${rect.y1}:${rect.x2}:${rect.y2}`;
      if (boxesByRegion.has(regionKey) && !current) {
        continue;
      }
      boxesByRegion.set(regionKey, {
        id: h.id,
        left: rect.x1 * scale,
        top: rect.y1 * scale,
        width: (rect.x2 - rect.x1) * scale,
        height: (rect.y2 - rect.y1) * scale,
        current,
        crossLayer,
        outlineOnly: !crossLayer && overlayShown
      });
    }
    return Array.from(boxesByRegion.values());
  }

  buildPieceDisplayHtml(text: string, isCurrent: boolean = false,
                        currentOccurrenceIndex: number | null = null): string {
    const term = this.normalizeForSearch(this.activeSearchTerm.trim());
    if (!this.searchPerformed || term.length < 1) {
      return this.escapeHtml(text);
    }
    return this.markSearchTerm(text, term, isCurrent, currentOccurrenceIndex);
  }

  private normalizeForSearch(value: string): string {
    return (value || '').normalize('NFD').replace(COMBINING_DIACRITICS_REGEX, '').toLowerCase();
  }

  private findTermRanges(text: string, normalizedTerm: string): Array<[number, number]> {
    if (!text || !normalizedTerm) {
      return [];
    }
    const chars = Array.from(text);
    const normalizedChars: string[] = [];
    const indexMap: number[] = [];
    for (let i = 0; i < chars.length; i++) {
      const normalized = this.normalizeForSearch(chars[i]);
      for (const ch of normalized) {
        normalizedChars.push(ch);
        indexMap.push(i);
      }
    }
    const haystack = normalizedChars.join('');
    const ranges: Array<[number, number]> = [];
    let pos = haystack.indexOf(normalizedTerm);
    while (pos > -1) {
      ranges.push([indexMap[pos], indexMap[pos + normalizedTerm.length - 1] + 1]);
      pos = haystack.indexOf(normalizedTerm, pos + normalizedTerm.length);
    }
    return ranges;
  }

  private markSearchTerm(text: string, normalizedTerm: string, isCurrent: boolean,
                         currentOccurrenceIndex: number | null = null): string {
    const chars = Array.from(text);
    const ranges = this.findTermRanges(text, normalizedTerm);
    if (!ranges.length) {
      return this.escapeHtml(text);
    }
    const targetOccurrence = currentOccurrenceIndex === null
      ? null
      : Math.min(currentOccurrenceIndex, ranges.length - 1);
    let out = '';
    let last = 0;
    ranges.forEach(([start, end], occurrenceIndex) => {
      const isCurrentOccurrence = isCurrent
        && (targetOccurrence === null || targetOccurrence === occurrenceIndex);
      const markClass = isCurrentOccurrence ? 'search-mark search-mark--current' : 'search-mark';
      out += this.escapeHtml(chars.slice(last, start).join(''))
        + '<span class="' + markClass + '">' + this.escapeHtml(chars.slice(start, end).join('')) + '</span>';
      last = end;
    });
    out += this.escapeHtml(chars.slice(last).join(''));
    return out;
  }

  private currentDisplayLayer(): MatchedTextLayer {
    return this.shouldShowTranslationOverlay() ? 'translated' : 'original';
  }

  isMatchVisibleInCurrentLayer(matchedIn?: MatchedTextLayer): boolean {
    // 'both' (term identical in both layers) and legacy/absent markers are
    // always on screen; otherwise the match is visible only in its own layer.
    if (!matchedIn || matchedIn === 'both') {
      return true;
    }
    return this.currentDisplayLayer() === matchedIn;
  }

  private coveringPieceRect(h: DocumentTextHighlight): { x1: number, y1: number, x2: number, y2: number } | null {
    const pieces = this.translationPiecesByPage.get(this.activePageTrueIndex) || [];
    const centerX = (h.x1 + h.x2) / 2;
    const centerY = (h.y1 + h.y2) / 2;
    const piece = this.pieceContaining(pieces, centerX, centerY);
    if (!piece) {
      return null;
    }
    return { x1: piece.ulc.x, y1: piece.ulc.y, x2: piece.lrc.x, y2: piece.lrc.y };
  }

  clearSearch(keepText: boolean = false): void {
    this.searchSub?.unsubscribe();
    this.searchLoading = false;
    this.pdfSearchComponent?.resetSearchSubject();
    this.resetSearchState(keepText);
    this._cdr.detectChanges();
  }

  goToNextMatch(): void {
    if (!this.searchHighlights.length) {
      return;
    }
    this.setActiveHighlightIndex(this.activeHighlightIndex + 1);
  }

  goToPreviousMatch(): void {
    if (!this.searchHighlights.length) {
      return;
    }
    this.setActiveHighlightIndex(this.activeHighlightIndex - 1);
  }

  private setActiveHighlightIndex(nextIndex: number): void {
    if (!this.searchHighlights.length) {
      this.activeHighlightIndex = -1;
      this.activeHighlightId = null;
      this.activeHighlightPageIndex = -1;
      this._cdr.detectChanges();
      return;
    }

    const normalizedIndex = ((nextIndex % this.searchHighlights.length) + this.searchHighlights.length) % this.searchHighlights.length;
    this.activeHighlightIndex = normalizedIndex;
    this.activeHighlightId = this.searchHighlights[normalizedIndex].id;
    this.updateActiveHighlightPageIndex();
    this.scrollToActiveHighlight(false);
    this._cdr.detectChanges();
  }

  scrollToActiveHighlight(forceCenter = false): void {
    if (this.activeHighlightId === null) {
      return;
    }
    if (this.activeHighlightPageIndex >= 0 && this.activePageTrueIndex !== this.activeHighlightPageIndex) {
      this.activePageTrueIndex = this.activeHighlightPageIndex;
    }
    setTimeout(() => this.scrollSidebarToActivePage());
    this.scrollToHighlightElement(forceCenter, 3);
  }

  private scrollToHighlightElement(forceCenter: boolean, retriesLeft: number): void {
    const delay = retriesLeft >= 3 ? 0 : 250;
    setTimeout(() => {
      if (this.activeHighlightId === null) {
        return;
      }
      const highlightEl = this._elementRef.nativeElement.querySelector(
        `[data-highlight-id="${this.activeHighlightId}"]`
      );
      if (highlightEl) {
        highlightEl.scrollIntoView({
          behavior: 'auto',
          block: forceCenter ? 'center' : 'nearest',
          inline: forceCenter ? 'center' : 'nearest'
        });
      } else if (retriesLeft > 0) {
        this.scrollToHighlightElement(forceCenter, retriesLeft - 1);
      }
    }, delay);
  }

  setBoxDrawingWidth(): void {
    const el = document.getElementById('redesigned-box-drawer-scroll-container');
    if (el) {
      this.boxDrawingWidth = el.clientWidth - 32;
    } else {
      const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      this.boxDrawingWidth = (windowWidth / 3) - 45;
    }
  }

  private areBoxesEqual(box1: Box, box2: Box): boolean {
    if (!box1 || !box2) return false;
    
    // Compare coordinates with small tolerance for floating point precision
    const tolerance = 0.001;
    return Math.abs(box1.x1 - box2.x1) < tolerance &&
           Math.abs(box1.y1 - box2.y1) < tolerance &&
           Math.abs(box1.x2 - box2.x2) < tolerance &&
           Math.abs(box1.y2 - box2.y2) < tolerance;
  }

  /**
   * Compares boxes using normalized coordinates (position as fraction of page dimensions).
   * This allows comparison across different coordinate spaces (e.g., API vs display-scaled).
   * Falls back to pixel comparison if page dimensions are missing or invalid.
   */
  private areBoxesSameRegion(box1: Box, box2: Box): boolean {
    if (!box1 || !box2) {
      return false;
    }
    
    const w1 = box1.pageWidth;
    const h1 = box1.pageHeight;
    const w2 = box2.pageWidth;
    const h2 = box2.pageHeight;
    
    // Fallback to pixel comparison if dimensions are missing or invalid
    if (!w1 || !h1 || !w2 || !h2 || w1 <= 0 || h1 <= 0 || w2 <= 0 || h2 <= 0) {
      return this.areBoxesEqual(box1, box2);
    }
    
    // Compare normalized coordinates with tolerance for floating-point precision
    const tolerance = 0.0001;
    return (
      Math.abs(box1.x1 / w1 - box2.x1 / w2) < tolerance &&
      Math.abs(box1.x2 / w1 - box2.x2 / w2) < tolerance &&
      Math.abs(box1.y1 / h1 - box2.y1 / h2) < tolerance &&
      Math.abs(box1.y2 / h1 - box2.y2 / h2) < tolerance
    );
  }

  trackHighlightById(index: number, item: { id: string }): string {
    return item.id;
  }

  swapLanguage(): void {
    this.viewInNativeLanguage = !this.viewInNativeLanguage;
    this._cdr.detectChanges();
    this._translationViewState.set(this.documentFile?.id, this.viewInNativeLanguage);
  }

  retryTranslation(): void {
    this.loadTranslations();
  }

  private loadTranslations(): void {
    if (!this.useAzureEarlyClassificationUI || !this.documentFile?.id) {
      return;
    }
    this.translationLoading = true;
    this.translationFailed = false;
    this.translationSub?.unsubscribe();
    this.translationSub = this._documentFileService.getPageTranslations(this.documentFile.id)
      .pipe(finalize(() => {
        this.translationLoading = false;
        this._cdr.detectChanges();
      }))
      .subscribe({
        next: (resp) => this.applyTranslations(resp),
        error: (err) => {
          this.translationFailed = true;
          this.logger.error('Failed to load page translations', { errorObject: err });
        }
      });
  }

  private applyTranslations(resp: PageTranslationsResponse): void {
    this.translationPiecesByPage = new Map<number, TranslationPiece[]>();
    this.pageIsRtlByPage = new Map<number, boolean>();
    (resp?.pages || []).forEach(page => {
      const pieces = page.pieces || [];
      this.translationPiecesByPage.set(page.pageNumber, pieces);
      this.pageIsRtlByPage.set(page.pageNumber, this.pageContainsRtl(pieces));
    });
    this.translationAvailable = !!resp?.translationAvailable;
    if (this.translationAvailable) {
      this.viewInNativeLanguage = this._translationViewState.get(this.documentFile?.id);
    }
  }

  activePageHasTranslation(): boolean {
    const pieces = this.translationPiecesByPage.get(this.activePageTrueIndex) || [];
    return pieces.some(p => this.shouldTranslatePiece(p));
  }

  shouldShowTranslationOverlay(): boolean {
    return !this.viewInNativeLanguage
      && !this.translationFailed
      && !this.translationLoading
      && this.activePageHasTranslation();
  }

  shouldShowTranslationBanner(): boolean {
    if (this.viewInNativeLanguage) {
      return false;
    }
    return this.translationFailed || (!this.translationLoading && !this.activePageHasTranslation());
  }

  get pageIsRtl(): boolean {
    return this.pageIsRtlByPage.get(this.activePageTrueIndex) || false;
  }

  translationDisplayText(piece: TranslationPiece): string {
    return this.shouldTranslatePiece(piece) ? piece.translatedRawText : piece.rawText;
  }

  private shouldTranslatePiece(piece: TranslationPiece): boolean {
    if (!piece.translatedRawText || piece.translatedRawText === piece.rawText) {
      return false;
    }
    if (piece.isNumeric) {
      return this.containsArabicIndicDigits(piece.rawText);
    }
    return true;
  }

  private containsArabicIndicDigits(text: string): boolean {
    for (const character of text || '') {
      const code = character.charCodeAt(0);
      const isArabicIndic = code >= ReviewTableIdentificationComponent.ARABIC_INDIC_DIGIT_START
        && code <= ReviewTableIdentificationComponent.ARABIC_INDIC_DIGIT_END;
      const isExtendedArabicIndic = code >= ReviewTableIdentificationComponent.EXTENDED_ARABIC_INDIC_DIGIT_START
        && code <= ReviewTableIdentificationComponent.EXTENDED_ARABIC_INDIC_DIGIT_END;
      if (isArabicIndic || isExtendedArabicIndic) {
        return true;
      }
    }
    return false;
  }

  getTranslationPiecesForCurrentPage(): Array<{
    id: string, left: number, top: number, width: number, height: number,
    fontSize: number | null, lineClamp: number | null, isParagraph: boolean,
    text: string, html: string, tooltip: string
  }> {
    const pieces = (this.translationPiecesByPage.get(this.activePageTrueIndex) || [])
      .filter(p => !p.isNumeric || this.shouldTranslatePiece(p));
    if (!pieces.length) {
      return [];
    }
    const renderedWidth = this.boxDrawingWidth * this.imgScale;
    const currentPiece = this.currentMatchPiece();
    const currentOccurrenceIndex = this.currentMatchOccurrenceIndex();
    return pieces.map((piece, idx) => {
      const pageWidth = piece.pageSize?.width || 1;
      const scale = renderedWidth / pageWidth;
      const boxHeight = (piece.lrc.y - piece.ulc.y) * scale;
      const fontSize = piece.fontSize && piece.fontSize > 0
        ? piece.fontSize * scale
        : ReviewTableIdentificationComponent.DEFAULT_TRANSLATION_FONT_SIZE_PX * scale;
      const lineHeightPx = fontSize * ReviewTableIdentificationComponent.PARAGRAPH_LINE_HEIGHT_RATIO;
      const lineClamp = piece.isParagraph ? Math.max(1, Math.ceil(boxHeight / lineHeightPx)) : null;
      const contentHeight = lineClamp ? lineClamp * lineHeightPx : boxHeight;
      const height = contentHeight + (lineClamp ? ReviewTableIdentificationComponent.TRANSLATION_PIECE_BORDER_Y_PX : 0);
      const text = this.translationDisplayText(piece);
      return {
        id: `translation-${this.activePageTrueIndex}-${idx}`,
        left: piece.ulc.x * scale,
        top: piece.ulc.y * scale,
        width: (piece.lrc.x - piece.ulc.x) * scale,
        height,
        fontSize,
        lineClamp,
        isParagraph: piece.isParagraph,
        text,
        html: this.buildPieceDisplayHtml(text, piece === currentPiece, currentOccurrenceIndex),
        tooltip: this.buildTranslationTooltip(text, piece === currentPiece, currentOccurrenceIndex),
      };
    });
  }

  private currentMatchOccurrenceIndex(): number | null {
    const active = this.searchHighlights[this.activeHighlightIndex];
    return active?.occurrenceIndex ?? null;
  }

  private currentMatchPiece(): TranslationPiece | null {
    const active = this.searchHighlights[this.activeHighlightIndex];
    if (!active || active.pageNumber - 1 !== this.activePageTrueIndex) {
      return null;
    }
    const pieces = (this.translationPiecesByPage.get(this.activePageTrueIndex) || []).filter(p => !p.isNumeric);
    const centerX = (active.x1 + active.x2) / 2;
    const centerY = (active.y1 + active.y2) / 2;
    return this.pieceContaining(pieces, centerX, centerY);
  }

  private buildTranslationTooltip(text: string, isCurrent: boolean = false,
                                  currentOccurrenceIndex: number | null = null): string {
    const html = this.buildPieceDisplayHtml(text, isCurrent, currentOccurrenceIndex);
    return `<span class="translation-tooltip__label">Full translation:</span> ${html}`;
  }

  private escapeHtml(text: string): string {
    return (text || '')
      .split('&').join('&amp;')
      .split('<').join('&lt;')
      .split('>').join('&gt;')
      .split('"').join('&quot;')
      .split("'").join('&#39;');
  }

  trackTranslationPieceById(index: number, item: { id: string }): string {
    return item.id;
  }

  private pageContainsRtl(pieces: TranslationPiece[]): boolean {
    return pieces.some(p => this.containsRtlText(p.rawText));
  }

  private containsRtlText(text: string): boolean {
    if (!text) {
      return false;
    }
    for (const char of text) {
      const code = char.codePointAt(0);
      if (ReviewTableIdentificationComponent.RTL_RANGES.some(([start, end]) => code >= start && code <= end)) {
        return true;
      }
    }
    return false;
  }

}
